import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { categories } from "@/mocks/categories";
import { site } from "@/mocks/site";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8cr3p14a1h3mr6f70";
const MAX_LENGTH = 500;

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-300";
const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-800";

const tips = [
  "尽量写清事件的时间、地点与大致经过，先讲事实，再补充你的判断。",
  "涉及他人姓名、电话、证件号等个人信息，请提前打码或隐去。",
  "聊天截图、通知原文、文件说明都可以在正文中描述，我们会一并归档。",
  "联系方式为选填项，留空同样可以提交，不影响线索进入流程。",
];

function SubmitPage() {
  const [contentLength, setContentLength] = useState(0);
  const { status, message, submit, reset } = useFormSubmit(
    SUBMIT_ADDR,
    "线索已提交成功，感谢你的信任。我们会在 48 小时内完成首轮归类，必要时再与你核对细节。"
  );

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "匿名爆料投稿 - 爆料平台",
      description: "通过匿名入口提交内部消息、身边事或维权线索，无需注册，联系方式为选填项。",
      url: "https://gregbennettguitars.com.cn/submit",
      inLanguage: "zh-CN",
    }),
    []
  );

  useSeo({
    title: "匿名爆料投稿入口 - 无需注册，可提交图文材料 | 爆料平台",
    description:
      "爆料平台匿名投稿入口：无需注册账号，可直接提交内部消息、公众事件、身边事或维权线索，联系方式为选填项，平台在 48 小时内完成首轮归类。",
    keywords: "匿名爆料入口,线索提交,内部消息投稿,维权线索,身边事投稿",
    path: "/submit",
    jsonLd,
  });

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "匿名爆料" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            匿名爆料投稿
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {site.tagline}。不需要注册账号，也不强制留下联系方式。把材料交给我们，剩下的交给流程。
          </p>
        </div>
      </div>

      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            {status === "success" ? (
              <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-checkbox-circle-line text-3xl" />
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">
                  提交成功
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-700">
                  {message}
                </p>
                <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={reset}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-add-line" />
                    再提交一条
                  </button>
                  <Link
                    to="/clues"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-medium text-foreground-800 transition-colors hover:bg-background-50"
                  >
                    浏览线索归档
                  </Link>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={submit}
                noValidate={false}
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="type" className={labelClass}>
                      爆料类型
                    </label>
                    <select id="type" name="type" defaultValue="internal" className={inputClass}>
                      {categories.map((category) => (
                        <option key={category.slug} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                      <option value="其他">其他（我来描述）</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="region" className={labelClass}>
                      所在地区 <span className="text-foreground-400">（选填）</span>
                    </label>
                    <input
                      id="region"
                      name="region"
                      type="text"
                      placeholder="例如：华东 · 某城区"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="title" className={labelClass}>
                    线索标题
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    required
                    placeholder="用一句话概括你想公开的事情"
                    className={inputClass}
                  />
                </div>

                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="content" className={labelClass}>
                      事件经过
                    </label>
                    <span className="mb-1.5 text-xs text-foreground-400">
                      {contentLength}/{MAX_LENGTH}
                    </span>
                  </div>
                  <textarea
                    id="content"
                    name="content"
                    required
                    rows={7}
                    maxLength={MAX_LENGTH}
                    onChange={(event) => setContentLength(event.target.value.length)}
                    placeholder="请描述时间、地点、涉及对象与大致经过，越具体越容易被核实。"
                    className={`${inputClass} resize-none leading-relaxed`}
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="material" className={labelClass}>
                    可提供的材料 <span className="text-foreground-400">（选填）</span>
                  </label>
                  <input
                    id="material"
                    name="material"
                    type="text"
                    placeholder="例如：聊天截图 6 张、通知原文一份、缴费凭证"
                    className={inputClass}
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="contact" className={labelClass}>
                    联系方式 <span className="text-foreground-400">（选填）</span>
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    type="text"
                    placeholder="邮箱或其它可联系到你的方式，留空亦可提交"
                    className={inputClass}
                  />
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && (
                  <p className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5" />
                    {message}
                  </p>
                )}

                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-foreground-500">
                    提交即表示你同意平台在保护身份的前提下整理与公开相关内容。
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        正在提交
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill" />
                        提交线索
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">投稿前提醒</h2>
                <ul className="mt-4 space-y-3">
                  {tips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-600">
                      <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-secondary-600" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-accent-300 bg-accent-50 p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-lock-2-line text-accent-600" />
                  关于匿名
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  平台默认不要求实名，也不强制填写联系方式。身份信息只做最小化留存，用于必要时核对细节，不会向第三方提供。
                </p>
                <Link
                  to="/faq"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600"
                >
                  查看隐私与核实说明
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">反馈周期</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{site.reviewCycle}</p>
                <p className="mt-3 text-xs text-foreground-500">{site.workTime}</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default SubmitPage;